/**
 * Promotion Calculation Utilities
 * Handles madregot (step-pricing) promotions with CUMULATIVE logic
 * Steps and max are computed across ALL items in the promotion
 */

// ============================================
// Basic Helpers
// ============================================

/**
 * Get unique makats from promotion items array
 * @param {Object} promo - Promotion object
 * @returns {string[]} Array of unique makats
 */
export function getPromoMakats(promo) {
  if (!promo?.items || !Array.isArray(promo.items)) return []
  const makats = promo.items.map((item) => item.makat).filter(Boolean)
  return [...new Set(makats)].sort() // sorted for deterministic order
}

/**
 * Check if promo contains a specific makat
 * @param {Object} promo
 * @param {string} makat
 * @returns {boolean}
 */
export function promoContainsMakat(promo, makat) {
  if (!promo || !makat) return false
  return getPromoMakats(promo).includes(makat)
}

/**
 * Sum total qty across all promo makats
 * @param {Object} params
 * @param {string[]} params.promoMakats - Makats in the promo
 * @param {Object} params.cartLinesByMakat - Cart lines map (makat -> { qty })
 * @param {Object} [params.pendingDeltaByMakat] - Optional pending deltas (makat -> deltaQty)
 * @returns {number} Total quantity
 */
export function sumPromoQty({ promoMakats, cartLinesByMakat, pendingDeltaByMakat = {} }) {
  let total = 0
  for (const makat of promoMakats) {
    const cartLine = cartLinesByMakat[makat]
    const cartQty = cartLine?.qty || 0
    const delta = pendingDeltaByMakat[makat] || 0
    total += Math.max(0, cartQty + delta)
  }
  return total
}

// ============================================
// Step Parsing
// ============================================

/**
 * Parse step config from promotion
 * @param {Object} step - Step object { amount, price, description }
 * @returns {{ amount: number, price: number, description: string } | null}
 */
function parseStep(step) {
  if (!step) return null

  const amount = parseInt(step.amount, 10)
  const price = parseFloat(step.price)

  if (isNaN(amount) || amount <= 0) return null
  if (isNaN(price) || price < 0) return null

  return {
    amount,
    price,
    description: step.description || '',
  }
}

/**
 * Get all valid steps from a promotion, sorted by amount ascending
 * @param {Object} promo
 * @returns {Array<{ stepKey: string, amount: number, price: number, description: string }>}
 */
export function getPromoSteps(promo) {
  if (!promo || promo.saleType !== 'madregot') return []

  const steps = []
  const stepKeys = ['step_1', 'step_2', 'step_3', 'step_4', 'step_5']

  for (const key of stepKeys) {
    const parsed = parseStep(promo[key])
    if (parsed) {
      steps.push({ stepKey: key, ...parsed })
    }
  }

  steps.sort((a, b) => a.amount - b.amount)
  return steps
}

/**
 * Pick the best madregot step for a given total qty
 * Uses min(totalQty, maxQtyForPricing) for step selection
 * @param {Object} promo
 * @param {number} totalQty - Total cumulative qty
 * @param {number} maxQtyForPricing - Max qty eligible for promo pricing (promo.max or Infinity)
 * @returns {{ stepKey: string, thresholdAmount: number, unitPrice: number, description: string } | null}
 */
export function pickMadregotStepForTotalQty(promo, totalQty, maxQtyForPricing) {
  const steps = getPromoSteps(promo)
  if (steps.length === 0) return null

  const qtyForStepSelection = Math.min(totalQty, maxQtyForPricing)
  if (qtyForStepSelection <= 0) return null

  let matchedStep = null
  for (const step of steps) {
    if (qtyForStepSelection >= step.amount) {
      matchedStep = step
    } else {
      break
    }
  }

  if (!matchedStep) return null

  return {
    stepKey: matchedStep.stepKey,
    thresholdAmount: matchedStep.amount,
    unitPrice: matchedStep.price,
    description: matchedStep.description,
  }
}

// ============================================
// Allocation
// ============================================

/**
 * Allocate promo qty across makats by addedAt order
 * Items added earlier get promo units first; only ONE makat can have
 * both promoQty > 0 and regularQty > 0 (the boundary item).
 * Items after the boundary are fully regular (no promo split).
 * @param {Object} params
 * @param {string[]} params.promoMakats - Makats in promo
 * @param {Object} params.cartLinesByMakat - Cart lines map (makat -> { qty, addedAt })
 * @param {Object} [params.pendingDeltaByMakat] - Optional pending deltas
 * @param {number} params.cappedPromoQty - Max qty eligible for promo pricing
 * @returns {Object} Map of makat -> { promoQty, regularQty }
 */
export function allocatePromoQtyByMakat({
  promoMakats,
  cartLinesByMakat,
  pendingDeltaByMakat = {},
  cappedPromoQty,
}) {
  const allocations = {}

  // Sort makats by addedAt ASC (items added earlier get promo first)
  // Tie-breaker: makat ASC for determinism
  const sortedMakats = [...promoMakats]
    .filter((m) => {
      const cartQty = cartLinesByMakat[m]?.qty || 0
      const delta = pendingDeltaByMakat[m] || 0
      return cartQty + delta > 0
    })
    .sort((a, b) => {
      const aAddedAt = cartLinesByMakat[a]?.addedAt || 0
      const bAddedAt = cartLinesByMakat[b]?.addedAt || 0
      if (aAddedAt !== bAddedAt) return aAddedAt - bAddedAt
      return a.localeCompare(b)
    })

  let remainingPromoQty = cappedPromoQty
  let boundaryReached = false

  for (const makat of sortedMakats) {
    const cartLine = cartLinesByMakat[makat]
    const cartQty = cartLine?.qty || 0
    const delta = pendingDeltaByMakat[makat] || 0
    const totalQtyForMakat = Math.max(0, cartQty + delta)

    if (totalQtyForMakat === 0) {
      allocations[makat] = { promoQty: 0, regularQty: 0 }
      continue
    }

    // After boundary, all items are fully regular
    if (boundaryReached) {
      allocations[makat] = { promoQty: 0, regularQty: totalQtyForMakat }
      continue
    }

    const promoQtyForMakat = Math.min(totalQtyForMakat, remainingPromoQty)
    const regularQtyForMakat = totalQtyForMakat - promoQtyForMakat

    allocations[makat] = {
      promoQty: promoQtyForMakat,
      regularQty: regularQtyForMakat,
    }

    remainingPromoQty -= promoQtyForMakat

    // If this item exhausted promo quota (has regularQty), it's the boundary
    if (remainingPromoQty <= 0) {
      boundaryReached = true
    }
  }

  // Add zero allocations for makats not in cart
  for (const makat of promoMakats) {
    if (!allocations[makat]) {
      allocations[makat] = { promoQty: 0, regularQty: 0 }
    }
  }

  return allocations
}

// ============================================
// Main Engine Function
// ============================================

/**
 * Compute cumulative madregot promotion application
 * @param {Object} params
 * @param {Object} params.promo - Promotion object
 * @param {Object} params.cartLinesByMakat - Cart lines map (makat -> { qty })
 * @param {Object} [params.pendingDeltaByMakat] - Optional pending deltas for preview
 * @returns {Object} Application result
 */
export function computeCumulativeMadregotApplication({
  promo,
  cartLinesByMakat,
  pendingDeltaByMakat = {},
}) {
  const promoMakats = getPromoMakats(promo)

  if (promoMakats.length === 0) {
    return {
      promoId: promo?.id || null,
      promoMakats: [],
      totalQty: 0,
      maxQty: null,
      cappedPromoQty: 0,
      appliedStep: null,
      allocationsByMakat: {},
      meta: {
        label: promo?.label || '',
        description: promo?.description || '',
        isOverMax: false,
        overMaxUnits: 0,
      },
    }
  }

  // Calculate total qty across all promo makats
  const totalQty = sumPromoQty({ promoMakats, cartLinesByMakat, pendingDeltaByMakat })

  // Parse max
  const maxQty = promo?.max ? parseInt(promo.max, 10) : null
  const hasMax = maxQty !== null && !isNaN(maxQty) && maxQty > 0

  // Capped promo qty
  const cappedPromoQty = hasMax ? Math.min(totalQty, maxQty) : totalQty

  // Over max info
  const isOverMax = hasMax && totalQty > maxQty
  const overMaxUnits = isOverMax ? totalQty - maxQty : 0

  // Pick step based on capped qty
  const appliedStep = pickMadregotStepForTotalQty(promo, totalQty, cappedPromoQty)

  // Allocate across makats
  const allocationsByMakat = allocatePromoQtyByMakat({
    promoMakats,
    cartLinesByMakat,
    pendingDeltaByMakat,
    cappedPromoQty,
  })

  return {
    promoId: promo?.id || null,
    promoMakats,
    totalQty,
    maxQty: hasMax ? maxQty : null,
    cappedPromoQty,
    appliedStep,
    allocationsByMakat,
    meta: {
      label: promo?.label || '',
      description: promo?.description || '',
      first_order: promo?.first_order || false,
      weeklyBenefit: promo?.weeklyBenefit || false,
      isOverMax,
      overMaxUnits,
    },
  }
}

// ============================================
// Next Step Hint
// ============================================

/**
 * Get hint for next step based on cumulative qty
 * @param {Object} promo
 * @param {number} totalQty - Current cumulative qty
 * @returns {{ nextStep: Object, unitsNeeded: number } | null}
 */
export function getNextStepHint(promo, totalQty) {
  if (!promo || totalQty <= 0) return null

  const steps = getPromoSteps(promo)
  if (steps.length === 0) return null

  // Consider max cap
  const maxQty = promo?.max ? parseInt(promo.max, 10) : null
  const hasMax = maxQty !== null && !isNaN(maxQty) && maxQty > 0

  // Find next step
  for (const step of steps) {
    if (step.amount > totalQty) {
      // Check if next step is reachable within max
      if (hasMax && step.amount > maxQty) {
        return null // Can't reach next step due to max
      }
      return {
        nextStep: step,
        unitsNeeded: step.amount - totalQty,
      }
    }
  }

  return null // Already at highest step
}

// ============================================
// Eligibility Helpers
// ============================================

/**
 * Check if user is eligible for a promotion
 * @param {Object} promo
 * @param {boolean} isFirstOrderEligible - Whether user's client.first_order === true
 * @returns {boolean}
 */
export function isEligibleForPromo(promo, isFirstOrderEligible) {
  if (!promo) return false
  if (promo.first_order === true && !isFirstOrderEligible) {
    return false
  }
  return true
}

/**
 * Filter promotions by eligibility
 * @param {Array} promos
 * @param {boolean} isFirstOrderEligible
 * @returns {Array}
 */
export function filterEligiblePromos(promos, isFirstOrderEligible) {
  if (!promos) return []
  return promos.filter((p) => isEligibleForPromo(p, isFirstOrderEligible))
}

/**
 * Sort promos by priority: end_date ASC, then id ASC
 * @param {Array} promos
 * @returns {Array}
 */
export function sortPromosByPriority(promos) {
  if (!promos) return []
  return [...promos].sort((a, b) => {
    // Compare end_date
    const endA = a.end_date || a.date_end_date?.toDate?.()?.toISOString?.() || '9999'
    const endB = b.end_date || b.date_end_date?.toDate?.()?.toISOString?.() || '9999'
    const dateCompare = String(endA).localeCompare(String(endB))
    if (dateCompare !== 0) return dateCompare

    // Tie-break by id
    return String(a.id || '').localeCompare(String(b.id || ''))
  })
}

// ============================================
// Promo Label Helpers
// ============================================

/**
 * Get promo label for display
 * @param {Object} promo
 * @returns {string}
 */
export function getPromoLabel(promo) {
  if (!promo) return ''
  if (promo.first_order) return 'מבצע הזמנה ראשונה'
  if (promo.weeklyBenefit) return 'הטבה שבועית'
  if (promo.saleType === 'madregot') return 'מבצע מדרגות'
  return promo.label || 'מבצע'
}

/**
 * Check if promo is madregot type
 * @param {Object} promo
 * @returns {boolean}
 */
export function isMadregotPromo(promo) {
  return promo?.saleType === 'madregot'
}

// ============================================
// DEPRECATED - Keep for backwards compatibility but do not use
// ============================================

/** @deprecated Use computeCumulativeMadregotApplication instead */
export function getBestPromoForQty(promos, qty) {
  console.warn('getBestPromoForQty is deprecated. Use computeCumulativeMadregotApplication.')
  return null
}

/** @deprecated Use computeCumulativeMadregotApplication instead */
export function getEffectiveUnitPrice(catalogPrice, eligiblePromos, qty) {
  console.warn('getEffectiveUnitPrice is deprecated. Use computeCumulativeMadregotApplication.')
  return { unitPrice: catalogPrice, isPromo: false, promo: null, step: null }
}

/** @deprecated Use allocatePromoQtyByMakat instead */
export function enforceMaxCap(promo, requestedQty, currentCartQty = 0) {
  console.warn('enforceMaxCap is deprecated. Use computeCumulativeMadregotApplication.')
  return { allowed: requestedQty, capped: false, message: null }
}
